// ATC Operator Console design tokens — ATC-109 / `ATC-ADR-018`.
//
// THIS MODULE IS THE ONLY IN-APP DECLARATION OF A CONTRACT VALUE. Every value
// below is a copy of `docs/vendor/rubix-ui-tokens.json`, which is Flight Deck's
// UI brand contract vendored at `contractVersion` 1.1.0 (`ATC-107`). The
// contract is the producer; ATC is a consumer. On divergence FLIGHTDECK WINS
// and ATC re-vendors — never edit `docs/vendor/`, and never hand-pick a hex
// here that the contract does not carry.
//
// `tokenParity.test.ts` reads the vendored JSON off disk and asserts that every
// value in this file matches it, so a drifted hex is a failing test rather than
// a twelfth palette.
//
// Two things this module deliberately is NOT:
//
// 1. It is NOT a projection of the whole contract. The contract is a SUITE
//    document and carries things ATC does not render: `surface.dark` (only
//    flight-agent's kiosk is a full dark surface — ATC's dark top bar is CHROME
//    INSIDE a light surface, which does not make the console dark-mode), the
//    Fluent semantic names (ATC has no Fluent and cannot resolve
//    `color="success"`), and the end-user surface rules (ATC has no end-user
//    surface). Parity is one-directional: every value HERE must match the
//    contract; the contract may carry more.
//
// 2. It is NOT the running stylesheet. `src/styles/tokens.css` declares the
//    same values as `--rx-*` custom properties for CSS to consume. Both exist
//    because TS components need the values as strings and CSS cannot import a
//    TS module. They are kept in step by `tokenParity.test.ts`, which checks
//    the CSS too.

/**
 * Accent — Rubix orange, in FOUR SLOTS.
 *
 * `ATC-ADR-018` anchor 3. THIS IS NOW THE PUBLISHED CONTRACT, not an ATC
 * deviation from it. `1.1.0`'s §2b mapped ATC's `--accent` to `#EA580C` while
 * §1.1's own ramp table assigned that same rung to graphical marks — and ATC
 * consumes the accent as `a { color: … }` and `.audit`, 13px BODY TEXT, which
 * needs 4.5:1 against `#EA580C`'s 3.56:1. Applied literally it would have
 * replaced a conformant blue (`#4b8bf5`, 5.06:1) with a LESS conformant orange:
 * precisely the failure the contract exists to stop, repeated as a role
 * assignment rather than as a wrong ratio. ATC reported it; flightdeck fixed it
 * at source in `1.1.1` and published this four-slot shape as canonical
 * (`brand.accent._accentAsText`, `_accentAsGraphical`).
 *
 * The lesson the producer drew is the one worth keeping HERE too: their ratio
 * gate recomputed every published ratio and was GREEN throughout, because
 * `#EA580C` at 3.56:1 is a CORRECT number — published as a graphical assertion
 * with min 3.0. A RIGHT NUMBER IN A WRONG ROLE PASSES A RATIO CHECK. Ratios,
 * computed:
 *
 *   #F97316 on #FFFFFF   2.80   fails even the 3:1 graphical floor
 *   #EA580C on #FFFFFF   3.56   clears 3:1 graphical, FAILS 4.5:1 text
 *   #C2410C on #FFFFFF   5.18   passes
 *   #201F1E on #F97316   5.87   passes — white would be 2.80
 *
 * DO NOT collapse these to one accent, and NEVER put `#fff` on the accent.
 */
export const ACCENT = {
  /**
   * Contract `brand.accent.base`. FILLS ONLY — `.btn-primary` background, the
   * 3px nav active indicator, a tint wash. Never text, never an
   * information-bearing mark on a light surface (2.80:1).
   */
  base: '#F97316',
  /**
   * Contract `brand.accent.hover`. The accent carrying INFORMATION as a
   * graphical object: a chart segment, a legend swatch, a state dot. 3.56:1 on
   * white clears WCAG 1.4.11's 3:1 for graphical objects, and nothing else.
   */
  graphic: '#EA580C',
  /**
   * Contract `brand.accent.pressed`. ACCENT TEXT — links, `.audit`. 5.18:1 on
   * white, which is what 13px body text requires.
   */
  text: '#C2410C',
  /** Contract `brand.accent.tint`. Wash behind an accent chip or active row. */
  tint: '#FFF7ED',
  /**
   * Contract `brand.accent.onBase`. Text sitting ON the accent is DARK INK.
   * This is the value most likely to be "corrected" back to white; white is
   * 2.80:1 and fails both AA and the 3:1 large-text floor.
   *
   * `.btn-danger` is a separate case and KEEPS white text: white on `#DC2626`
   * is 4.83:1. Only `.btn-primary` carries the white-on-orange trap.
   */
  onBase: '#201F1E',
} as const

/**
 * Identity — the mark, the loader, the login moment, print.
 *
 * IDENTITY IS NOT INTERACTION (§1.2). The gradient never appears on an
 * interactive control alongside the accent ramp and never conveys state. The
 * corporate orange is the brand library's primary and is deliberately NOT the
 * software accent — white on `#EA7400` is 3.00:1, so it could not carry white
 * button text either.
 */
export const IDENTITY = {
  gradientFrom: '#FFC107',
  gradientMid: '#EA7400',
  gradientTo: '#F0441D',
  corporatePrimary: '#EA7400',
  corporateHover: '#C25E12',
  /**
   * The animated loader mark's negative space. Deliberately NOT
   * `SURFACE.canvas` (`#F5F3F0`) — the contract says so explicitly. Do not
   * "correct" it.
   */
  markNegativeSpace: '#F4F2EE',
} as const

/**
 * Light-mode surfaces. ATC is a light console with dark CHROME.
 *
 * `surface.dark` from the contract is deliberately absent: it is a full-surface
 * mode and today only flight-agent's ENROUTE progress screen uses it. A console
 * does not get to be dark because it has a dark bar.
 */
export const SURFACE = {
  /**
   * The page ground. Warm, deliberately NOT zinc-50 (`#FAFAFA`) — the warmth is
   * the one thing that stops a dense operator grid reading as a spreadsheet.
   *
   * STATUS DOTS AND CHIPS MUST NOT SIT DIRECTLY ON THIS. On the warm canvas
   * success is 2.98:1 and warning 2.88:1, both under the 3:1 graphical floor.
   * They sit on `base` (white), inside a `Panel` — which is why `Panel` is
   * load-bearing for conformance rather than cosmetic.
   */
  canvas: '#F5F3F0',
  /** White. The panel fill, and the only surface a status dot may sit on. */
  base: '#FFFFFF',
  rowFill: '#FAF9F8',
  rowFillAlt: '#F3F2F1',
  topBar: '#1B1A19',
  topBarText: '#F3F2F1',
  topBarTextMuted: '#A19F9D',
  /**
   * The dark table band. Load-bearing, not decoration: it connects a group
   * header to its expanded micro-table. Without it the header floats and the
   * rows below read as an unrelated table.
   */
  tableBand: '#484644',
  tableBandText: '#F3F2F1',
} as const

/**
 * Strokes. SEPARATION COMES FROM 1px STROKES AND FILL STEPS — NO SHADOWS.
 * A shadow on an operator surface reads as a marketing card.
 */
export const STROKE = {
  base: '#E1DFDD',
  subtle: '#EDEBE9',
} as const

export const TEXT = {
  primary: '#201F1E',
  secondary: '#605E5C',
  /**
   * `#A19F9D` is 2.64:1 on white. Conformant ONLY for genuinely disabled or
   * inactive controls, which WCAG 1.4.3 exempts.
   *
   * IT IS NOT A COLOUR FOR CONTENT, and specifically not for a micro-label —
   * the label that tells the reader what a number means. That fails AA at any
   * size and fails badly at 11px. The Flight Deck console does exactly this in
   * three primitives today; the contract records it as a defect on the
   * reference implementation, not as a licence.
   */
  disabled: '#A19F9D',
  onDark: '#F3F2F1',
} as const

/**
 * Status — five meanings, ONE spelling here.
 *
 * The contract publishes each status twice: a Fluent v9 semantic name AND a
 * hex. ATC HAS NO FLUENT and cannot resolve `color="success"` at all, so ATC
 * uses the hex. That is not a downgrade — it is the dual-spelling rule working
 * as designed, and it is why the contract carries both.
 *
 * THE HEXES ARE GRAPHICAL VALUES AND CANNOT BE RENDERED AS TEXT. On white:
 * success 3.30, warning 3.19 — both fail 4.5:1; on the warm canvas they fall to
 * 2.98 and 2.88, failing even 3:1. Flight Deck never hits this because a Fluent
 * tinted `Badge` supplies its own foreground. So ATC's chip is TINT BACKGROUND +
 * HEX DOT + `TEXT.primary` LABEL, on a WHITE panel and never on the canvas.
 *
 * SINCE `1.1.1` THAT IS THE CONTRACT'S OWN PRESCRIPTION, not ATC's workaround:
 * `status._hexIsNotATextColour` says the hex is a dot/bar/icon value and the
 * label is `text.light.primary` on the tint, and it says so because ATC built
 * the first hex-consuming chip. The mapping from an ATC value to a tone lives
 * in `statusTone.ts`.
 */
export const STATUS = {
  /** Terminal and fine. Nothing to do. */
  success: { hex: '#16A34A', tint: '#F0FDF4' },
  /** A genuine failure. Something is broken. */
  error: { hex: '#DC2626', tint: '#FEF2F2' },
  /** A HUMAN MUST ACT. Not a crash. Also where an unrecognized value lands. */
  warning: { hex: '#D97706', tint: '#FFFBEB' },
  /**
   * In progress. Blue, and deliberately NOT the accent: warning `#D97706` sits
   * at hue 32° and the accent `#F97316` at 25°, seven degrees apart, so
   * "in progress" and "a human must act" would become two shades of one orange.
   *
   * NO ATC SURFACE CONSUMES THIS TODAY, and that absence is deliberate —
   * `suspended` maps to `warning`, not to `underway`. The tone is implemented
   * so the primitive is complete. Do not go looking for a use for it.
   */
  underway: { hex: '#2563EB', tint: '#EFF6FF' },
  /**
   * Not started, not applicable, nothing to report.
   *
   * NEVER let a real status value fall through to this. `FD-197` was a faulted
   * device that rendered as a neutral grey chip and looked benign in the device
   * grid for as long as the terminal vocabulary went unhandled.
   */
  neutral: { hex: '#605E5C', tint: '#F3F2F1' },
} as const

/**
 * Type. ONE UI FACE, ONE MONO FACE, AND EVERY NUMERIC IS MONO WITH TABULAR
 * FIGURES — counts, timestamps, serials, GUIDs. A column of proportional digits
 * cannot be scanned, and an operator scanning at 11pm is the design target.
 */
export const TYPE = {
  /**
   * The contract's font stacks, VERBATIM — and since `1.1.1` these are also the
   * stacks CSS uses, because the fontsource family names now come FIRST.
   *
   * There used to be a separate `FONT_STACK` here. `1.1.0`'s `type.web.ui` named
   * only `"Geist"` while its `type.web.delivery` prescribed
   * `@fontsource-variable/geist`, which registers faces named `'Geist Variable'`
   * and `'Geist Mono Variable'` — there is no face named `"Geist"` on that route,
   * so shipping the contract string literally fell back to `system-ui` SILENTLY,
   * with the fonts downloaded and paid for on the wire. ATC found it on adoption;
   * flightdeck fixed it at source in `1.1.1` (`type.web._fontsourceTrap`), so the
   * local override is DELETED rather than kept. Both spellings are now in the
   * stack: the Variable names cover fontsource, the bare names cover the
   * Google-CDN route Flight Deck's own console still uses.
   *
   * Fontsource, not the Google Fonts CDN: self-hosted survives an egress proxy,
   * and ATC's Static Web App sets `Cache-Control: no-store`.
   */
  ui: '"Geist Variable", "Geist", system-ui, -apple-system, sans-serif',
  mono: '"Geist Mono Variable", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  uiWeights: [400, 500, 600, 700, 800],
  monoWeights: [400, 500],
} as const

/**
 * The type scale. The 13px body and the 11px uppercase micro-label are the
 * console's two workhorses and are what give it its density.
 */
export const TYPE_SCALE = {
  pageTitle: { size: 24, weight: 600, tracking: '-0.02em' },
  metricValue: { size: 20, weight: 400, mono: true, tabularNums: true },
  factValue: { size: 14, weight: 400 },
  body: { size: 13, weight: 400 },
  microLabel: { size: 11, weight: 600, case: 'upper', tracking: '0.08em' },
  fieldLabel: { size: 11, weight: 500, case: 'upper', tracking: '0.06em' },
  monoIdentifier: { size: 12, mono: true },
} as const

/**
 * Geometry. TOKEN-ERA SURFACES CARRY NO RADIUS — the 8px rounded card is the
 * pre-token treatment and is not the direction. Chips get 2px; pills get a pill.
 */
/**
 * ATC-OWNED DIVERGENCES from `GEOMETRY`, kept SEPARATE from it on purpose.
 *
 * `GEOMETRY` above must keep matching the vendored contract byte for byte —
 * that is what makes an upstream republish visible. A divergence therefore
 * cannot be expressed by editing it; it has to sit beside it, named, with the
 * contract value it replaces recorded next to it.
 *
 * `radiusPanel` — the contract publishes `geometry.radius.panel: 0` and says
 * "Token-era surfaces carry NO radius. The 8px rounded card is the pre-token
 * treatment and is not the direction." ATC renders 4px instead, on operator
 * direction (2026-09-10). This is a house-style preference on an internal
 * operator surface, not a defect in the contract: unlike the three things ATC
 * reported and got fixed at source in `1.1.1`, there is no arithmetic here that
 * makes the published value wrong — Flight Deck is entitled to its own answer,
 * and so is the operator whose console this is. Reported upstream as a
 * preference rather than a bug, and re-vendoring is unaffected because the
 * contract half is untouched.
 */
export const GEOMETRY_ATC_OVERRIDES = {
  /** Contract says 0. See above. */
  radiusPanel: 4,
} as const

export const GEOMETRY = {
  radius: { chip: 2, panel: 0, pill: 9999 },
  strokeWidth: 1,
  activeIndicatorWidth: 3,
  shell: {
    topBarHeight: 48,
    navWidth: 240,
    navBreakpointPx: 1024,
    contentMaxWidth: 1400,
    pagePadding: 32,
  },
  spacingBase: 4,
} as const

/**
 * Icons. ONE library, ONE visual weight, no mixing.
 * `@fluentui/react-icons` and `@phosphor-icons/react` are banned by name.
 */
export const ICON = {
  library: 'lucide-react',
  strokeWidth: 1.5,
  sizes: { inline: 16, dense: 14, micro: 12, section: 20, emptyState: 32 },
} as const
